import {useMemo, useState} from 'react';
import './system-entities-index-list-card.scss';
import moment from 'moment';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Pallete} from '../../theme/bluebird';
import {AffiliatedUser} from '../../backend/responses/affiliated-user';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  SxProps,
  Typography,
} from '@mui/material';
import Confetti from 'react-confetti';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {LuBedSingle} from 'react-icons/lu';
import {FiEdit, FiGift, FiTrash2} from 'react-icons/fi';
import {ComboActions} from '../combo-actions/combo-actions';
import {DeleteUserModal} from '../delete-user-modal/delete-user-modal';
import {ColumnIdentifier} from '../../screens/private/office/professional-caregiver/professional-caregivers/_root/professional-caregivers.professional-caregiver.screen';
import {UserType} from '../../backend/enums/user-type';
import {getLocalizedFieldValue} from '../../utils/functions/get-localized-field-value';
import {PaginatedTableColumn} from '../../interfaces/paginated-table-data';

interface SystemEntitiesIndexListCardProps {
  data: AffiliatedUser;
  columns: PaginatedTableColumn[];
  toLink: string;
  canWrite?: boolean;
  canDelete?: boolean;
  onDelete?: (data: AffiliatedUser) => void;
  onExportPdf?: (data: AffiliatedUser) => void;
}

export const SystemEntitiesIndexListCard = ({
  data,
  toLink,
  canWrite,
  canDelete,
  onDelete,
  onExportPdf,
  columns,
}: SystemEntitiesIndexListCardProps) => {
  const comboActionsWrapperSx: SxProps = {
    color: 'var(--dcm-gray-light)',
    display: 'flex',
    justifyContent: 'flex-end',
  };

  const history = useHistory();

  const {t} = useTranslation();

  //Dialog
  const [deleteDialogOpened, setDeleteDialogOpened] = useState<boolean>(false);

  // Handle delete dialog opened state
  const handleDeleteDialogOpened = (opened: boolean) => {
    // Change dialog state
    setDeleteDialogOpened(opened);
  };

  // Get senior social responses (data.affiliation.socialResponses has the ids of the needed social responses, and data.entitySocialResponses has the data of the social responses)
  const getSeniorSocialResponses = () => {
    if (data.affiliation.socialResponses && data.affiliation.socialResponses?.length > 0) {
      return data.affiliation.socialResponses?.map((socialResponseId) => {
        return data.entitySocialResponses.find(
          (socialResponse) => socialResponse.id === socialResponseId
        );
      });
    } else {
      return [];
    }
  };

  // Get first social response color (avatar border)
  const getFirstSocialResponseColor = (data: AffiliatedUser) => {
    return data.entitySocialResponses[0].foregroundColor || '';
  };

  const seniorAge = (): number | string => {
    if (!data.user.birthday) return '-';
    const birthDate = moment(data.user.birthday);
    return moment().diff(birthDate, 'years');
  };

  const caregiverRoles = useMemo(() => {
    return (
      data.user.type === UserType.Caregiver &&
      columns.find((col) => col.columnId === ColumnIdentifier.Roles)?.value
    );
  }, [columns, data.user.type]);
  const caregiverRolesText = caregiverRoles || '-';

  const actions: Array<any> = [];
  if (canWrite && data.user.isActive) {
    actions.push({
      label: t(translations.user_card.actions.edit),
      icon: {element: FiEdit},
      callback: () => history.push(toLink ?? ''),
    });
  }
  if (canDelete) {
    actions.push({
      label: t(translations.user_card.actions.delete),
      icon: {element: FiTrash2},
      callback: () => handleDeleteDialogOpened(true),
    });
  }
  if (data.user.isActive && onExportPdf) {
    actions.push({
      label: t(translations.export.senior_profile.save_pdf),
      icon: {element: SaveAltIcon},
      callback: () => onExportPdf(data),
    });
  }
  const hasActions = actions.length > 0;

  const fakeDisabled = !data.user.isActive && data.user.type === UserType.Caregiver;
  return (
    <>
      <DeleteUserModal
        deleteDialogOpened={deleteDialogOpened}
        setDeleteDialogOpened={setDeleteDialogOpened}
        handleDeleteDialogOpened={handleDeleteDialogOpened}
        data={data}
        onDelete={onDelete}
      />
      <Card
        variant="outlined"
        className="system-entities-index-list-card card-outer-box"
        sx={!fakeDisabled ? {'&:hover': {backgroundColor: Pallete.blue.hover}} : undefined}
      >
        {/* EASTER EGG: Hey Developer! Do not talk about this to anyone. This is a secret. */}
        {data.user.birthday &&
          moment(data.user.birthday).format('MM-DD') === moment().format('MM-DD') && (
            <Box className="birthday-surprise">
              <Confetti width={200} height={226} />
            </Box>
          )}

        <CardActionArea
          disableRipple={!canWrite || !data.user.isActive}
          onClick={(event: any) => {
            if (toLink && !fakeDisabled) {
              history.push(toLink);
            }
          }}
          sx={{
            flex: 1,
            '&:hover .MuiCardActionArea-focusHighlight': {
              opacity: 0,
            },
            '&.MuiCardActionArea-root': {
              cursor: fakeDisabled ? 'default' : 'pointer',
            },
          }}
        >
          <CardHeader
            className={
              hasActions ? 'card-header edit-permissions' : 'card-header no-edit-permissions'
            }
            action={
              ((hasActions && (data.user.isActive || data.user.type === UserType.Senior)) ||
                (!data.user.isActive && UserType.Caregiver)) && (
                <IconButton
                  disableRipple
                  aria-label="settings"
                  onClick={(event) => event.stopPropagation()}
                >
                  <ComboActions
                    className="actions"
                    wrapperSX={comboActionsWrapperSx}
                    triggerSX={{height: '32px', width: '32px'}}
                    actions={actions}
                  />
                </IconButton>
              )
            }
            sx={{'& .MuiSvgIcon-root': {color: 'var(--dcm-gray-general)'}}}
          />
          <CardContent className="card-content">
            <Avatar
              className={
                data.user.type === UserType.Senior
                  ? 'avatar senior-avatar'
                  : 'avatar caregiver-avatar'
              }
              src={data.user.picture}
              alt={data.user.name}
              sx={{
                border:
                  data.user.type === UserType.Senior
                    ? data.entitySocialResponses.length > 0
                      ? `2px solid ${getFirstSocialResponseColor(data)}`
                      : 'none'
                    : '1px solid #b6b6b6',
              }}
            >
              {data.user.type === UserType.Senior ? '' : data.user.name.charAt(0).toUpperCase()}
            </Avatar>
            <Typography
              className={
                data.user.isActive || data.user.type === UserType.Senior
                  ? 'user-name active-or-senior'
                  : 'user-name inactive-caregiver'
              }
              align="center"
            >
              {data.user.name}
            </Typography>

            {data.user.type === UserType.Caregiver ? (
              <Typography
                title={caregiverRolesText !== '-' ? caregiverRolesText : undefined}
                className={
                  data.user.isActive
                    ? 'caregiver-affiliation active-caregiver'
                    : 'caregiver-affiliation inactive-caregiver'
                }
                align="center"
              >
                {caregiverRolesText}
              </Typography>
            ) : (
              <>
                <Box className="senior-info">
                  <FiGift size="20px" color="var(--dcm-gray)" />
                  <Typography className="text-content" align="center">
                    {seniorAge()} {t(translations.cards.years)}
                  </Typography>
                  {data.affiliation.room && (
                    <Box className="room-info">
                      <Divider className="divider" variant={'fullWidth'} orientation={'vertical'} />
                      <Box className="room-content">
                        <LuBedSingle size="20px" color="var(--dcm-gray)" />
                        <Typography className="text-content" align="center">
                          {data.affiliation.room}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                <Box className="social-responses-box">
                  {getSeniorSocialResponses().map((response, index) => (
                    <Chip
                      className="social-response-chip"
                      key={index}
                      label={getLocalizedFieldValue(response!, 'abbreviation')}
                      color="primary"
                      sx={{
                        backgroundColor: response!.backgroundColor,
                        color: response!.foregroundColor,
                        '& .MuiChip-label': {
                          px: '8px',
                        },
                      }}
                    />
                  ))}
                </Box>
              </>
            )}

            <Typography className="caregiver-pending-invitation" align="center" variant="subtitle2">
              {data.user.isActive || data.user.type === UserType.Senior
                ? ''
                : t(translations.cards.pendingInvitation)}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    </>
  );
};
